import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "../design/Button";
import { Modal } from "../design/Modal";
import { MAX_AVATAR_PIXELS } from "@/lib/upload-limits";

// Image cropper: drag to position, slider to zoom, returns the framed crop as
// { mimeType, content (base64, no data: prefix) }. Born as the square avatar cropper; the frame is
// a prop now, so the platform logo goes through the very same dialog (spark#314). Deepened rather
// than widened: a second cropper for a second shape would drift.

const FRAMES = {
  /** A face or a company mark: a circle, cover-fitted, JPEG (nothing transparent to keep). */
  circle: {
    view: { w: 280, h: 280 },
    output: { w: 512, h: 512 },
    mimeType: "image/jpeg",
    radius: "rounded-full",
    fit: "cover",
  },
  /**
   * The platform logo for the sidebar: 4:1 like the 160 by 40 slot it is drawn in. Contain-fitted,
   * so a square mark starts whole with room on both sides instead of cut to its middle band, and
   * PNG, so a transparent background survives onto whatever page colour the tenant chose.
   */
  wide: {
    view: { w: 280, h: 70 },
    output: { w: 640, h: 160 },
    mimeType: "image/png",
    radius: "rounded-md",
    fit: "contain",
  },
} as const;

export type CropFrame = keyof typeof FRAMES;

export interface CropResult {
  mimeType: string;
  content: string;
}

/**
 * Why the cropper can refuse a file the picker already accepted.
 *
 * `decode`: the browser could not decode it at all (a renamed non-image, a corrupt file). Nothing is
 * written in that case — `apply` is gated on `nat` — but without this the dialog sits there inert: an
 * empty circle, a dead Apply button and no reason given, so the user can only guess and cancel.
 *
 * `pixels`: it decodes but exceeds {@link MAX_AVATAR_PIXELS}. This is the bound that actually protects
 * the decode path, since bytes say nothing about how much memory a bitmap needs.
 */
export type AvatarRejection = "decode" | "pixels";

interface AvatarCropperProps {
  /** The shape and size of the crop, round by default. */
  frame?: CropFrame;
  file: File | null;
  onCancel: () => void;
  onApply: (result: CropResult) => void;
  // Called instead of rendering a broken frame. The host is expected to close the cropper and say
  // why — a message left behind an open modal is a message nobody reads.
  onInvalid?: (reason: AvatarRejection) => void;
}

interface Natural {
  w: number;
  h: number;
}

interface Pos {
  x: number;
  y: number;
}

interface DragState extends Pos {
  px: number;
  py: number;
}

export function AvatarCropper({ frame = "circle", file, onCancel, onApply, onInvalid }: AvatarCropperProps) {
  const intl = useIntl();
  const { view, output, mimeType, radius, fit } = FRAMES[frame];
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const [nat, setNat] = useState<Natural | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState<Pos>({ x: 0, y: 0 });
  const [touched, setTouched] = useState(false);

  // Create + revoke the object URL in one effect so they stay paired.
  // Splitting creation (useMemo) from revocation (separate cleanup) leaves a
  // revoked URL after StrictMode's dev mount→cleanup→mount replay, so the crop
  // preview never loads.
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  const coverScale = nat ? Math.max(view.w / nat.w, view.h / nat.h) : 1;
  // Where the zoom slider bottoms out: at cover the image can only ever fill the frame; at contain
  // it may sit inside it, which is what a wordmark in a 4:1 frame needs.
  const minZoom = fit === "contain" && nat ? Math.min(view.w / nat.w, view.h / nat.h) / coverScale : 1;
  const effScale = coverScale * zoom;
  const dw = nat ? nat.w * effScale : 0;
  const dh = nat ? nat.h * effScale : 0;

  // Keeps the image over the frame while it is larger than the frame, and inside it while it is
  // smaller: the two bounds swap sides exactly when the image crosses the frame's size.
  const within = (p: number, span: number, total: number): number =>
    Math.min(Math.max(0, total - span), Math.max(Math.min(0, total - span), p));
  const clamp = (p: Pos, w: number, h: number): Pos => ({
    x: within(p.x, w, view.w),
    y: within(p.y, h, view.h),
  });

  function onImgLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const w = e.currentTarget.naturalWidth;
    const h = e.currentTarget.naturalHeight;
    // The byte cap cannot bound this: the decoded bitmap costs ~4 bytes per pixel regardless of how
    // well the file compressed, so the pixel count is what has to be checked — and onLoad is the first
    // moment it is known. Note this therefore rejects AFTER the browser has decoded the image once: it
    // protects the canvas draw and the sustained preview, not that first decode. Bounding that too
    // would mean createImageBitmap before mounting.
    if (w * h > MAX_AVATAR_PIXELS) {
      // Clear any previously accepted size too, so the cropper cannot stay "ready" with a stale one
      // if the host decides to leave the dialog open rather than close it.
      setNat(null);
      onInvalid?.("pixels");
      return;
    }
    setNat({ w, h });
    const cover = Math.max(view.w / w, view.h / h);
    const start = fit === "contain" ? Math.min(view.w / w, view.h / h) / cover : 1;
    const dw0 = w * cover * start;
    const dh0 = h * cover * start;
    setZoom(start);
    setPos({ x: (view.w - dw0) / 2, y: (view.h - dh0) / 2 });
    setTouched(false);
  }

  function onZoom(next: number) {
    setZoom(next);
    setTouched(true);
    if (nat) {
      const w = nat.w * coverScale * next;
      const h = nat.h * coverScale * next;
      setPos((p) => clamp(p, w, h));
    }
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { px: e.clientX, py: e.clientY, ...pos };
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragRef.current) return;
    const d = dragRef.current;
    setTouched(true);
    setPos(clamp({ x: d.x + (e.clientX - d.px), y: d.y + (e.clientY - d.py) }, dw, dh));
  }

  function onPointerUp() {
    dragRef.current = null;
  }

  function apply() {
    const img = imgRef.current;
    /* v8 ignore next -- unreachable: the Apply button is `disabled={!nat}`, and `nat` is set only by the
       image's own load handler, so both the element and its intrinsic size are always present here */
    if (!img || !nat) return;
    const canvas = document.createElement("canvas");
    canvas.width = output.w;
    canvas.height = output.h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    // A source rectangle that reaches past the image (contain fit) is clipped by the canvas, and
    // the clipped part stays transparent on the PNG: that is the room beside a square wordmark.
    const sx = -pos.x / effScale;
    const sy = -pos.y / effScale;
    ctx.drawImage(img, sx, sy, view.w / effScale, view.h / effScale, 0, 0, output.w, output.h);
    const dataUrl = canvas.toDataURL(mimeType, 0.9);
    const content = dataUrl.slice(dataUrl.indexOf(",") + 1);
    onApply({ mimeType, content });
  }

  return (
    <Modal
      id="photo-cropper"
      open={file != null}
      onClose={onCancel}
      dismiss="explicit"
      dirty={touched}
      title={
        frame === "wide"
          ? intl.formatMessage({ id: "design.cropLogo", defaultMessage: "Crop logo" })
          : intl.formatMessage({ id: "design.cropPhoto", defaultMessage: "Crop photo" })
      }
      maxWidth="22rem"
      footer={
        <>
          <Button
            id="photo-cropper-cancel"
            variant="secondary"
            className="px-3 py-1 text-smycles-micro"
            onClick={onCancel}
          >
            {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
          </Button>
          <Button
            id="photo-cropper-apply"
            variant="primary"
            className="px-3 py-1 text-smycles-micro"
            onClick={apply}
            disabled={!nat}
          >
            {intl.formatMessage({ id: "design.apply", defaultMessage: "Apply" })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-4">
        <div
          className={`relative cursor-grab touch-none select-none overflow-hidden ${radius} bg-page ring-1 ring-ink/10 active:cursor-grabbing`}
          style={{ width: view.w, height: view.h }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {url ? (
            <img
              ref={imgRef}
              src={url}
              alt=""
              draggable={false}
              onLoad={onImgLoad}
              onError={() => onInvalid?.("decode")}
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                width: dw,
                height: dh,
                transform: `translate(${pos.x}px, ${pos.y}px)`,
                maxWidth: "none",
              }}
            />
          ) : null}
        </div>
        <input
          type="range"
          min={minZoom}
          max="3"
          step="0.01"
          value={zoom}
          onChange={(e) => onZoom(Number(e.target.value))}
          className="w-full accent-ink-accent"
          aria-label={intl.formatMessage({ id: "design.zoom", defaultMessage: "Zoom" })}
        />
      </div>
    </Modal>
  );
}
